import React from 'react';
import {
    mergeClasses,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    InlineDrawer,
    Button,
} from '@fluentui/react-components';
import {
    Dismiss20Regular,
    PanelRightContractRegular,
    PanelRightExpandRegular,
} from '@fluentui/react-icons';
import { CopilotIcon24Color } from 'owa-copilot-icon';
import { observer } from 'owa-mobx-react';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';
import { useAction } from 'owa-react-hooks/lib/useAction';
import {
    closeCopilotChatDrawer,
    isExpandCollapseButtonShown,
    isCopilotChatDrawerExpanded,
    toggleCopilotChatDrawerExpandCollapse,
    onCopilotChatDrawerReady,
    resizeCopilotChatDrawer,
    getCopilotChatDrawerTrueWidth,
    getShowBizChatInReadingPane,
} from 'owa-copilot-chat-drawer-store';
import { ResizeHandle, ResizeHandleScenario, ResizeHandleDirection } from 'owa-resize-handle';
import { M365ChatContainer } from 'owa-sidepane-bizchat';
import { getCopilotChatDrawerStyles } from './getCopilotChatDrawerStyles';
import {
    container,
    denselessMargin,
    drawerBody,
    drawerHeader,
    headerTitle,
    copilotIconAndTitle,
    drawerContainer,
} from './CopilotChatDrawer.scss';
import CopilotChatDrawerStrings from '../localization/CopilotChatDrawer.locstring.json';
import loc, { isCurrentCultureRightToLeft } from 'owa-localize';
import { isFeatureEnabled } from 'owa-feature-flags';
import { trace } from 'owa-trace';
import type { IContextualChat } from 'owa-bizchat-types/lib/types';
import { hasQueryStringParameter } from 'owa-querystring';
import type { MailboxInfo } from 'owa-client-types';

const dismissIcon = <Dismiss20Regular />;
const expandIcon = <PanelRightExpandRegular />;
const collapseIcon = <PanelRightContractRegular />;

export type CopilotChatDrawerProps = {
    mailboxInfo: MailboxInfo;
    targetWindow: Window; // The host window of current CopilotChat instance
    isVisible: boolean;
    dismissOverride?: () => void; // If provided, this function will be called when the user clicks the dismiss button
    hideExpandCollapseButton?: boolean; // If true, hide the expand/collapse button
    elementStyles?: Partial<
        Record<'drawerContainer' | 'drawerHeader' | 'inlineDrawer', React.CSSProperties>
    >; // If provided, these styles will be applied to the corresponding elements
    getChatContext?: () => Promise<IContextualChat>; // Function to get the chat context annotation
    overrideScenario?: string; // If provided, this scenario will be passed to the M365ChatContainer to override the default scenario
};

export const CopilotChatDrawer: React.FunctionComponent<CopilotChatDrawerProps> = observer(
    function CopilotChatDrawer({
        mailboxInfo,
        targetWindow,
        isVisible,
        dismissOverride,
        hideExpandCollapseButton,
        elementStyles,
        getChatContext,
        overrideScenario,
    }) {
        const { copilotPane, overrideFuiIconFont } = getCopilotChatDrawerStyles();
        const showExpandCollapseButton = hideExpandCollapseButton
            ? false
            : isExpandCollapseButtonShown(targetWindow);
        const isCopilotChatDrawerInExpandedState = isCopilotChatDrawerExpanded(targetWindow);
        const isRTL = isCurrentCultureRightToLeft();
        const isDenselessEnabled = isFeatureEnabled('rp-denseless');
        const drawerRef = React.useRef<HTMLDivElement>(null);
        const showInReadingPane = getShowBizChatInReadingPane(targetWindow);

        useAction(onCopilotChatDrawerReady);

        const handleExpandCollapseClick = React.useCallback(() => {
            toggleCopilotChatDrawerExpandCollapse(
                targetWindow,
                !isCopilotChatDrawerInExpandedState
            );
        }, [targetWindow, isCopilotChatDrawerInExpandedState]);

        const handleCloseClick = React.useCallback(() => {
            closeCopilotChatDrawer(targetWindow);
        }, [targetWindow]);

        const handleCopilotChatResized = React.useCallback(
            (_rpDimension: number, copilotChatDrawerWidth: number) => {
                resizeCopilotChatDrawer(targetWindow, copilotChatDrawerWidth);
            },
            [targetWindow]
        );

        const drawerHeaderActions = React.useMemo(() => {
            const expandCollapseButtonLabel = isCopilotChatDrawerInExpandedState
                ? loc(CopilotChatDrawerStrings.collapseCopilotChatDrawer)
                : loc(CopilotChatDrawerStrings.expandCopilotChatDrawer);

            const expandCollapseButtonIcon = isCopilotChatDrawerInExpandedState
                ? isRTL
                    ? expandIcon
                    : collapseIcon
                : isRTL
                ? collapseIcon
                : expandIcon;

            const closeButtonLabel = loc(CopilotChatDrawerStrings.closeCopilotChatDrawer);

            return (
                <>
                    {showExpandCollapseButton && (
                        <Button
                            appearance="transparent"
                            aria-label={expandCollapseButtonLabel}
                            title={expandCollapseButtonLabel}
                            onClick={handleExpandCollapseClick}
                            icon={expandCollapseButtonIcon}
                            onKeyDown={onExpandCollapseKeyDown}
                        />
                    )}
                    <Button
                        appearance="transparent"
                        aria-label={closeButtonLabel}
                        title={closeButtonLabel}
                        icon={dismissIcon}
                        onClick={dismissOverride ?? handleCloseClick}
                    />
                </>
            );
        }, [
            isCopilotChatDrawerInExpandedState,
            isRTL,
            showExpandCollapseButton,
            handleExpandCollapseClick,
            handleCloseClick,
            dismissOverride,
        ]);

        // Compute the style to make width of the drawer component
        const copilotChatDrawerWidth = getCopilotChatDrawerTrueWidth(targetWindow);
        const drawerContainerStyle = useComputedValue(() => {
            trace.info(` Copilot chat drawer view state: isVisible: ${isVisible}`, 'iframeWrapper');
            if (showInReadingPane) {
                return {
                    display: isVisible ? 'flex' : 'none',
                    flexGrow: 1,
                    ...(elementStyles?.drawerContainer ?? {}),
                };
            }
            return {
                width: `${copilotChatDrawerWidth}px`,
                display: isVisible ? 'flex' : 'none',
                ...(elementStyles?.drawerContainer ?? {}),
            };
        }, [copilotChatDrawerWidth, isVisible, elementStyles?.drawerContainer, showInReadingPane]);

        const drawerHeaderClasses = mergeClasses(drawerHeader, overrideFuiIconFont);

        const isCachingEnabled = useComputedValue(() => {
            if (targetWindow && targetWindow !== window) {
                return false;
            }
            // Need to disable caching in dev mode because of a React Strict issue to not
            // disrupt dev process, will enable once fixed by MetaOS HubSDK team.
            if (isFeatureEnabled('mon-copilot-bizchat-caching')) {
                return process.env.NODE_ENV === 'dev'
                    ? hasQueryStringParameter('bizchatCaching')
                    : true;
            }
            return false;
        }, []);

        return (
            <>
                {isVisible && !showInReadingPane && (
                    <ResizeHandle
                        direction={ResizeHandleDirection.vertical}
                        onResized={handleCopilotChatResized}
                        scenario={ResizeHandleScenario.CopilotChatDrawer}
                        data-resize-scenario={'CopilotChatDrawer'}
                    />
                )}
                <div
                    data-app-section={'CopilotChat'}
                    ref={drawerRef}
                    className={mergeClasses(container, isDenselessEnabled && denselessMargin)}
                    style={drawerContainerStyle}
                >
                    <InlineDrawer
                        position="end"
                        open={true}
                        className={drawerContainer}
                        style={elementStyles?.inlineDrawer}
                    >
                        <DrawerHeader className={drawerHeaderClasses}>
                            <DrawerHeaderTitle action={drawerHeaderActions}>
                                <div className={copilotIconAndTitle}>
                                    <CopilotIcon24Color />
                                    <span className={headerTitle}>Copilot</span>
                                </div>
                            </DrawerHeaderTitle>
                        </DrawerHeader>
                        <DrawerBody className={drawerBody}>
                            <div className={copilotPane}>
                                <M365ChatContainer
                                    mailboxInfo={mailboxInfo}
                                    isVisible={isVisible}
                                    targetWindow={targetWindow}
                                    getChatContext={getChatContext}
                                    overrideScenario={overrideScenario}
                                    isCachingEnabled={isCachingEnabled}
                                />
                            </div>
                        </DrawerBody>
                    </InlineDrawer>
                </div>
            </>
        );
    },
    'CopilotChatDrawer'
);

// Accessibility implement to ensure shift+tab on expand/collapse properly
// goes back to the Copilot Command Center split action primary button.
const onExpandCollapseKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab' && e.shiftKey) {
        e.preventDefault();
        document.getElementById('CopilotCommandCenterSplitButtonPrimaryAction')?.focus();
    }
};
